import { useEffect, useRef, useState } from 'react';
import { getCurrentUser, signOut as amplifySignOut } from 'aws-amplify/auth';
import { Header } from './components/layout/Header';
import { CenteredLayout } from './components/layout/CenteredLayout';
import { WelcomePage } from './features/Welcome';
import { HolidayFormPanel } from './features/HolidayFormPanel';
import { ItineraryPanel } from './features/ItineraryPanel';
import { GeneratingScreen } from './features/GeneratingPanel/GeneratingScreen';
import { EmailPanel } from './features/EmailPanel';
import { GoodbyePanel } from './features/GoodbyePanel';
import { CustomSignIn } from './features/Authentication/CustomSignIn';
import { configureAmplify, isApiKeyAuth } from './services/amplifyConfig';
import { GlobalStyles } from './styles/GlobalStyles';
import { theme } from './styles/theme';
import { AGENT_PHASES } from './constants';
import type { FormData } from './types';
import { useAgentUpdates } from './hooks/useAgentUpdates';
import { useAppNavigation } from './hooks/useAppNavigation';
import { useSurveySubmission } from './features/ItineraryPanel/useSurveySubmission';
import { MOCK_ITINERARY, createMockEvents, streamMockEvents } from './mocks/mockItinerary';
import logger from './utils/logger';

const DEVELOPMENT_MODE = process.env.REACT_APP_DEVELOPMENT_MODE === 'true';

configureAmplify();

// ── Read & consume URL params (email + raffle_code) once ─────────────
function consumeAuthParams(): { email?: string; raffleCode?: string } {
  const params = new URLSearchParams(window.location.search);
  const email = params.get('email') ?? undefined;
  const raffleCode = params.get('raffle_code') ?? undefined;
  if (email || raffleCode) {
    // Strip query params from the URL so they don't linger
    const url = new URL(window.location.href);
    url.searchParams.delete('email');
    url.searchParams.delete('raffle_code');
    window.history.replaceState({}, '', url.pathname + url.hash);
  }
  return { email, raffleCode };
}

const initialAuthParams = consumeAuthParams();

export default function App() {
  // ── Auth state ──────────────────────────────────────────────────────
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [authParams] = useState(initialAuthParams);

  // ── Session & form data ─────────────────────────────────────────────
  const [sessionId, setSessionId] = useState('');
  const [formData, setFormData] = useState<FormData | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  // ── Agent ───────────────────────────────────────────────────────────
  const agent = useAgentUpdates();
  const hasTriggeredRef = useRef(false);

  // ── Dev tools (mock mode & error simulation) ────────────────────────
  const [mockEnabled, setMockEnabled] = useState(false);
  const [errorSimEnabled, setErrorSimEnabled] = useState(false);
  const [mockItinerary, setMockItinerary] = useState<typeof agent.itinerary>(null);
  const [mockEvents, setMockEvents] = useState<typeof agent.events>([]);
  const mockStreamCleanupRef = useRef<(() => void) | null>(null);

  // ── Derived data (mock-aware) ───────────────────────────────────────
  const resolvedItinerary = mockEnabled ? mockItinerary : agent.itinerary;
  const resolvedEvents = mockEnabled ? mockEvents : agent.events;
  const { submit: submitSurvey } = useSurveySubmission();

  // ── State reset (called by nav hook whenever we land on welcome) ────
  const resetAppState = () => {
    agent.reset();
    mockStreamCleanupRef.current?.();
    setMockItinerary(null);
    setMockEvents([]);
    setEmailError(null);
    setSessionId('');
    setFormData(null);
  };

  // ── Navigation (phase, screen, hash sync, beforeunload) ─────────────
  const { screen, phase, nav } = useAppNavigation({
    hasItinerary: !!resolvedItinerary,
    onResetState: resetAppState,
  });

  // ── Effects ─────────────────────────────────────────────────────────

  // Check auth on mount (API key mode auto-authenticates)
  useEffect(() => {
    if (isApiKeyAuth) { setIsAuthenticated(true); return; }
    checkAuthStatus();
  }, []);

  // Auto-open sign-in when URL params were provided
  useEffect(() => {
    if (authParams.email || authParams.raffleCode) {
      nav.openSignIn();
    }
  }, []);

  // Sync agent phase → app phase (real mode only)
  useEffect(() => {
    if (mockEnabled || errorSimEnabled) return;
    if (agent.phase === 'running' && phase === AGENT_PHASES.RUNNING) return;
    if (agent.phase === 'complete') nav.goToComplete();
    if (agent.phase === 'error') nav.goToError();
  }, [agent.phase]);

  // Trigger agent call once when phase enters RUNNING (real mode only)
  useEffect(() => {
    if (mockEnabled || !formData) return;
    if (phase !== AGENT_PHASES.RUNNING || hasTriggeredRef.current) return;
    hasTriggeredRef.current = true;

    // Error sim: fake an error instead of calling the real agent
    if (errorSimEnabled) { setTimeout(() => nav.goToError(), 3000); return; }

    agent.generateItinerary({
      sessionId,
      userName: formData.userName || 'Guest',
      destination: formData.destination,
      departure: formData.departure,
      startDate: formData.startDate,
      endDate: formData.endDate,
      adults: Number(formData.adults) || 1,
      children: Number(formData.children) || 0,
      budget: formData.budget || 'mid',
      interests: formData.interests,
      requirements: formData.requirements,
    });
  }, [phase, sessionId, formData]);

  // ── Auth handlers ───────────────────────────────────────────────────

  const checkAuthStatus = async () => {
    try {
      const currentUser = await getCurrentUser();
      setIsAuthenticated(true);
      setUser(currentUser);
      logger.info('User authenticated', { username: currentUser.username });
    } catch {
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const handleSignInSuccess = () => {
    nav.closeSignIn();
    checkAuthStatus();
  };

  const handleSignOut = async () => {
    if (isApiKeyAuth) return;
    try {
      await amplifySignOut();
      setIsAuthenticated(false);
      setUser(null);
      handleReset();
    } catch (err) {
      logger.error('Sign out error', err);
    }
  };

  // ── Form submission ─────────────────────────────────────────────────

  const handleFormSubmit = (sid: string, input: Record<string, unknown>) => {
    const id = mockEnabled ? 'mock-session' : sid;
    setSessionId(id);
    setFormData(input as unknown as FormData);
    nav.goToRunning();

    if (mockEnabled) {
      startMockStream(id);
    } else {
      hasTriggeredRef.current = false;
    }
  };

  // ── Email & goodbye ─────────────────────────────────────────────────

  const handleEmailSubmit = (email: string) => {
    setEmailError(null);

    if (errorSimEnabled) {
      setEmailError("Simulated error — Connie couldn't send the itinerary.");
      return;
    }

    if (!mockEnabled) {
      submitSurvey({ sessionId, userName: formData?.userName || '', email })
        .catch((err) => logger.error('Failed to submit survey', err));
    }

    nav.goToGoodbye();
  };

  // ── Reset & retry ───────────────────────────────────────────────────

  const handleModifyTrip = () => {
    agent.reset();
    setSessionId('');
    nav.goToForm();
  };

  const handleRetryInPlace = () => {
    // Re-trigger the agent call with the same form data, fresh session
    agent.reset();
    hasTriggeredRef.current = false;
    const newId = mockEnabled ? 'mock-session' : `retry-${Date.now()}`;
    setSessionId(newId);
    if (mockEnabled) {
      setMockEvents([]);
      startMockStream(newId);
    }
    nav.goToRunning();
  };

  const handleReset = () => nav.resetToWelcome();

  // ── Mock mode helpers ───────────────────────────────────────────────

  const startMockStream = (id: string) => {
    setMockEvents([]);
    mockStreamCleanupRef.current?.();

    // Error sim: stream a few events then trigger error
    if (errorSimEnabled) {
      const timers: ReturnType<typeof setTimeout>[] = [];
      const partial = createMockEvents(id).slice(0, 4);
      partial.forEach((evt, i) => {
        timers.push(setTimeout(() => setMockEvents(prev => [...prev, evt]), 400 * (i + 1)));
      });
      const errorThinking = { sessionId: id, timestamp: new Date().toISOString(), eventType: 'THINKING' as const, message: "I wasn't able to find flights to the destination. The search API returned an unexpected error — this might be a temporary issue with availability data." };
      timers.push(setTimeout(() => setMockEvents(prev => [...prev, errorThinking]), 400 * partial.length + 400));
      timers.push(setTimeout(() => nav.goToError(), 400 * partial.length + 1200));
      mockStreamCleanupRef.current = () => timers.forEach(clearTimeout);
      return;
    }

    // Normal mock: stream all events then complete
    mockStreamCleanupRef.current = streamMockEvents(
      id,
      (events) => setMockEvents(events),
      () => {
        setMockItinerary({ ...MOCK_ITINERARY, sessionId: id, postcardUrl: undefined, status: 'COMPLETE' });
        nav.goToComplete();
      },
    );
  };

  // ── Render ──────────────────────────────────────────────────────────

  const renderPanel = () => {
    switch (screen) {
      case 'sign-in':
        return <CustomSignIn onSuccess={handleSignInSuccess} onBack={nav.closeSignIn} initialEmail={authParams.email} initialPassword={authParams.raffleCode} />;

      case 'welcome':
        return <WelcomePage onNext={nav.goToForm} isAuthenticated={isAuthenticated} onShowSignIn={nav.openSignIn} />;

      case 'form':
        return (
          <HolidayFormPanel
            onSubmit={handleFormSubmit}
            initialData={formData}
            sessionId={sessionId || undefined}
            onBack={resolvedItinerary ? nav.goToComplete : nav.goToWelcome}
          />
        );

      case 'generating':
      case 'error':
        return (
          <GeneratingScreen
            destination={formData?.destination || ''}
            agentEvents={resolvedEvents}
            hasError={phase === AGENT_PHASES.ERROR}
            onRetry={handleRetryInPlace}
            onModifyTrip={handleModifyTrip}
          />
        );

      case 'itinerary':
        return (
          <ItineraryPanel
            phase={phase}
            itinerary={resolvedItinerary}
            sessionId={sessionId}
            agentEvents={resolvedEvents}
            formData={formData}
            onRetry={handleRetryInPlace}
            onReset={handleReset}
            onModifyTrip={nav.goToModifyTrip}
            onAcceptItinerary={nav.goToEmail}
          />
        );

      case 'email':
        return <EmailPanel onSubmit={handleEmailSubmit} error={emailError} />;

      case 'goodbye':
        return <GoodbyePanel destination={formData?.destination || ''} onRestart={handleReset} />;

      default:
        return null;
    }
  };

  return (
    <>
      <GlobalStyles />
      <Header
        user={user}
        onSignOut={handleSignOut}
        developmentMode={DEVELOPMENT_MODE}
        errorSimEnabled={errorSimEnabled}
        onToggleErrorSim={() => setErrorSimEnabled(prev => !prev)}
        mockEnabled={mockEnabled}
        onToggleMock={() => setMockEnabled(prev => !prev)}
      />
      {mockEnabled && <DevBanner color={theme.colors.warning} textColor={theme.colors.warningText} label="Mock Mode — No agent calls" />}
      {errorSimEnabled && <DevBanner color={theme.colors.error} textColor={theme.colors.errorText} label="Error Mode — No agent calls" />}
      <CenteredLayout>{renderPanel()}</CenteredLayout>
    </>
  );
}

// ── Dev banner (mock/error mode indicator) ──────────────────────────
function DevBanner({ color, textColor, label }: { color: string; textColor: string; label: string }) {
  return (
    <div style={{
      background: color, color: textColor, textAlign: 'center',
      padding: '0.35rem', fontSize: '0.75rem', fontWeight: 700,
      letterSpacing: '0.1em', textTransform: 'uppercase',
    }}>
      ⚠ {label}
    </div>
  );
}
